import React from 'react';
import PropTypes from 'prop-types';

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-800',
  PARTIAL: 'bg-blue-100 text-blue-800',
  SETTLED: 'bg-green-100 text-green-800',
  WRITTEN_OFF: 'bg-gray-200 text-gray-600',
};

const STATUS_LABELS = {
  PENDING: 'Pending',
  PARTIAL: 'Partial',
  SETTLED: 'Settled',
  WRITTEN_OFF: 'Written Off',
};

const formatRands = (cents) => {
  const rands = typeof cents === 'number' ? cents / 100 : 0;
  return `R ${rands.toFixed(2)}`;
};

const formatBillingPeriod = (period) => {
  if (!period) return 'N/A';
  const [year, month] = period.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long' });
};

const formatEpoch = (epochMs) => {
  if (!epochMs) return 'N/A';
  return new Date(epochMs).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Displays connection fee charges for the logged-in resident.
 *
 * @param {Object}  props
 * @param {Object}  props.data          - Full response from the charges endpoint
 * @param {boolean} props.loading       - Whether the data is still loading
 * @param {Function} props.onRefresh    - Optional callback to re-fetch
 */
const ConnectionFeeCard = ({ data, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="card bg-beige-40 shadow-md rounded-lg p-6 mt-6">
        <h2 className="text-xl font-semibold mb-4">Monthly Fixed Charge</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-2">Loading connection fee data...</span>
        </div>
      </div>
    );
  }

  const charges = data?.charges ?? [];
  const totalOutstanding = data?.totalOutstandingRands ?? 0;

  return (
    <div className="card bg-beige-40 shadow-md rounded-lg p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Monthly Fixed Charge</h2>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        )}
      </div>

      {totalOutstanding > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-3 mb-4 rounded">
          <span className="font-semibold">Outstanding balance:</span>{' '}
          R {totalOutstanding.toFixed(2)}
        </div>
      )}

      {charges.length === 0 ? (
        <p className="text-gray-500">No connection fee charges.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing Period
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount Due
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outstanding
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {charges.map((charge) => (
                <tr key={charge.chargeUuid}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatBillingPeriod(charge.billingPeriod)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatRands(charge.amountDueCents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatRands(charge.amountDebitedCents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatRands(charge.amountOutstandingCents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        STATUS_STYLES[charge.status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {STATUS_LABELS[charge.status] || charge.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatEpoch(charge.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

ConnectionFeeCard.propTypes = {
  data: PropTypes.shape({
    totalOutstandingRands: PropTypes.number,
    chargeCount: PropTypes.number,
    charges: PropTypes.arrayOf(
      PropTypes.shape({
        chargeUuid: PropTypes.string,
        billingPeriod: PropTypes.string,
        amountDueCents: PropTypes.number,
        amountDebitedCents: PropTypes.number,
        amountOutstandingCents: PropTypes.number,
        status: PropTypes.string,
        createdAt: PropTypes.number,
        settledAt: PropTypes.number,
      })
    ),
  }),
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export default ConnectionFeeCard;
